/**
 * `skill-recommendations` — pure helpers that derive a Phase-1
 * suggestion surface for the onboarding Skills step. Per ADR-0050 §2.2
 * detection alone drives the merchant-facing suggestion panel; the
 * ADR-0040 LLM enrichment pipeline composes in via
 * {@link mergeLlmIntoBaseline} when (and only when) the flag flips and
 * a server result is available — the merge is the identity on the
 * `baseline` when `llm === null` (the dormant default in prod today).
 *
 * Mapping table (ADR-0050 §2.2, woocommerce widened post-ADR-0170 read
 * slice — see note below):
 *   - `shopify`              → 6 commerce skills (browse, check-availability,
 *                              order-status, track-shipment, request-refund,
 *                              recommend).
 *   - `woocommerce`          → 6 commerce skills (browse, check-availability,
 *                              order-status, track-shipment, request-refund,
 *                              recommend). `order_status` / `track_shipment` /
 *                              `recommend` are native reads on the
 *                              WooCommerce connector (ADR-0170,
 *                              `@sill/connectors-woocommerce` SUPPORTED_SKILLS);
 *                              `request_refund` rides the Stripe rail
 *                              (ADR-0173) once connected. `place_order` is
 *                              deliberately withheld here: ADR-0173 lights it
 *                              only once Stripe is connected, which this
 *                              function has no visibility into at
 *                              onboarding time (ADR-0174 payment-link handoff
 *                              for non-Stripe merchants is designed, not
 *                              built) — surfacing it unconditionally would
 *                              re-introduce the false-green problem this
 *                              table exists to avoid.
 *   - `wordpress` (cart-free)→ browse + recommend.
 *   - `other_known | unknown`→ browse only.
 *   - detection failed/null  → no panel (empty list — the caller hides
 *                              the panel when nothing comes back).
 *
 * All `skill_id` values are members of the canonical seven-default
 * `SkillId` enum (`@sill/types`). The helper never invents an id; every
 * row is one of the seven baseline skills.
 */

import {
  BUSINESS_PAGE_KINDS,
  BUSINESS_PAGE_KIND_TO_SKILL_ID,
  isBusinessPageKind,
} from '@sill/types';
import type {
  BusinessPageKind,
  BusinessSkillId,
  ProfileExtractionResult,
  ProfileSuggestedSkill,
  ReadinessPlatformKind,
  SkillId,
} from '@sill/types';
import type { DetectPlatformResult, SiteScanRow } from '@sill/types/api';

/**
 * ADR-0186 S4 — confidence floor for a scan-suggested skill to be
 * PRE-TOGGLED. At or above this, the scan's evidence is strong enough
 * to check the box unpersisted (the merchant sees it on and can
 * un-toggle). A missing confidence (pre-v4 rows had no score) is
 * treated as ABOVE the floor — an unscored suggestion the model still
 * chose to emit is a positive signal, matching how the pre-v4 pipeline
 * already behaved. Mirrors the ADR's "≥0.6 pre-toggled" contract.
 */
export const SCAN_PRETOGGLE_CONFIDENCE = 0.6;

/**
 * One suggested skill row for the dashboard's onboarding suggestion
 * panel. `source` lets the panel distinguish the quiet detection-driven
 * default from the higher-confidence LLM enrichment (badge differs per
 * ADR-0040 §3.2 defense 7).
 */
export interface SkillRecommendation {
  skill_id: SkillId;
  /** <= 256 chars, plain text, rendered as inert escaped React text at
   *  the view boundary. Detection rationales are static strings written
   *  here; LLM rationales are server-validated per ADR-0040 §3.2 and
   *  defensively re-bounded at render time in `SkillsList`. */
  rationale: string;
  source: 'detection' | 'llm';
}

const sid = (s: string): SkillId => s as SkillId;

/**
 * Pure detection → recommendation mapping. Closed table; every branch
 * returns a subset of the seven canonical skills. Detection-failed or
 * null returns an empty list so the panel hides cleanly.
 */
export function recommendSkillsForDetection(
  detected: DetectPlatformResult | null,
): SkillRecommendation[] {
  if (detected === null) return [];

  switch (detected.platform) {
    case 'shopify': {
      const rationale =
        'Commerce backend detected — these skills map to Shopify APIs.';
      // `request_refund` is a checkout skill: it only lights once the
      // Stripe rail is on, which this helper has no visibility into at
      // detection time. Surfacing it as "DETECTED" alongside its NEEDS
      // PAYMENT pill was contradictory — the row stayed off and the
      // merchant could not understand why. Omit it here; Settings →
      // Skills still shows it with the real gating reason.
      return [
        { skill_id: sid('browse_catalog'), rationale, source: 'detection' },
        { skill_id: sid('check_availability'), rationale, source: 'detection' },
        { skill_id: sid('order_status'), rationale, source: 'detection' },
        { skill_id: sid('track_shipment'), rationale, source: 'detection' },
        { skill_id: sid('recommend'), rationale, source: 'detection' },
      ];
    }
    case 'woocommerce': {
      const rationale =
        'WooCommerce detected — commerce skills available; agent-cart wiring partial.';
      // Same reasoning as Shopify: `request_refund` is Stripe-rail-gated
      // (ADR-0173), so it should not surface as a DETECTED default.
      return [
        { skill_id: sid('browse_catalog'), rationale, source: 'detection' },
        { skill_id: sid('check_availability'), rationale, source: 'detection' },
        { skill_id: sid('order_status'), rationale, source: 'detection' },
        { skill_id: sid('track_shipment'), rationale, source: 'detection' },
        { skill_id: sid('recommend'), rationale, source: 'detection' },
      ];
    }
    case 'wordpress': {
      const rationale =
        'Content site — browse-for-info and recommend; no cart detected.';
      return [
        { skill_id: sid('browse_catalog'), rationale, source: 'detection' },
        { skill_id: sid('recommend'), rationale, source: 'detection' },
      ];
    }
    case 'other_known': {
      // A known-but-not-commerce platform — browse is the safe floor.
      const rationale =
        'Known platform — start with browse and add more as needed.';
      return [
        { skill_id: sid('browse_catalog'), rationale, source: 'detection' },
      ];
    }
    case 'unknown':
      // We could NOT fingerprint the site at all — most often because the
      // store sits behind a WAF/bot filter that blocks our server-side
      // probe (Cloudflare, Shopify bot management, etc.). Returning a
      // single-skill recommendation here made the grid narrow to
      // browse_catalog only, which reads as "Sill decided against your
      // other skills" — the opposite of the truth (we simply couldn't
      // see). Fail OPEN: an empty list keeps all seven defaults on (the
      // SkillsList narrowing effect no-ops on empty), so an unscannable
      // site is not silently down-scoped. (Founder 2026-07-14.)
      return [];
    default:
      return [];
  }
}

/**
 * Compose the dormant ADR-0040 LLM result into the detection baseline.
 * When `llm === null` (flag off / run failed / dormant — the prod
 * default today) returns `baseline` unchanged. When a result is present
 * the LLM rows SHADOW the detection rows for the same `skill_id` (the
 * LLM source is higher-confidence by construction); skills present only
 * in `baseline` are preserved; skills present only in the LLM result
 * are appended.
 *
 * The merge is order-stable: baseline order is preserved, LLM-only rows
 * append at the end. The function performs no IO and never throws.
 */
export function mergeLlmIntoBaseline(
  baseline: SkillRecommendation[],
  llm: ProfileExtractionResult | null,
): SkillRecommendation[] {
  if (llm === null) return baseline;
  if (llm.status !== 'complete') return baseline;
  const llmRows = llm.suggested_skills ?? [];
  if (llmRows.length === 0) return baseline;

  const llmById = new Map<string, ProfileSuggestedSkill>();
  for (const row of llmRows) {
    // First write wins — the server-side post-validator (ADR-0040 §3.2
    // defense 2) already de-duplicates by skill_id, but a defensive
    // Map.has() guard keeps this helper independent of that contract.
    if (!llmById.has(row.skill_id)) {
      llmById.set(row.skill_id, row);
    }
  }

  const merged: SkillRecommendation[] = [];
  const seen = new Set<string>();
  for (const row of baseline) {
    const llmRow = llmById.get(row.skill_id);
    if (llmRow !== undefined) {
      merged.push({
        skill_id: llmRow.skill_id,
        rationale: llmRow.rationale,
        source: 'llm',
      });
    } else {
      merged.push(row);
    }
    seen.add(row.skill_id);
  }
  for (const row of llmRows) {
    if (seen.has(row.skill_id)) continue;
    merged.push({
      skill_id: row.skill_id,
      rationale: row.rationale,
      source: 'llm',
    });
    seen.add(row.skill_id);
  }
  return merged;
}

/**
 * ADR-0186 S4 — fold a completed site scan's suggested skills into the
 * detection baseline. This is the scan-grounding merge: the ADR-0184
 * pipeline produces per-skill `confidence` + evidence `rationale`, and
 * those become the merchant's pre-toggled starting set with the reason
 * shown inline.
 *
 * Rules (order-stable, pure, never throws):
 *   - `scan === null`, not completed, or no suggestions → `baseline`
 *     unchanged. The Skills step ALWAYS has the detection baseline to
 *     fall back on, so a failed/absent scan never empties the panel.
 *   - A scan row at or above `SCAN_PRETOGGLE_CONFIDENCE` SHADOWS the
 *     matching baseline row (its evidence rationale replaces the static
 *     detection copy) and appends if the baseline lacked it — these are
 *     the pre-toggled suggestions (`source: 'llm'` drives the SILL
 *     ANALYZER badge + the pre-toggle in SkillsList).
 *   - A scan row BELOW the floor is dropped from the merged set: it is
 *     a weak signal, so it neither shadows the baseline nor forces a
 *     toggle. (Surfacing sub-floor suggestions as unchecked rows is a
 *     later refinement; the safe default is to not over-toggle.)
 *   - Every skill_id is filtered against the closed enum by SkillsList
 *     at render; this helper additionally ignores rows whose id is not
 *     already a known baseline id OR a plausible skill string — but it
 *     does NOT invent ids.
 */
export function mergeScanIntoBaseline(
  baseline: SkillRecommendation[],
  scan: SiteScanRow | null,
): SkillRecommendation[] {
  if (scan === null || scan.status !== 'completed') return baseline;
  const rows = scan.suggested_skills ?? [];
  if (rows.length === 0) return baseline;

  const byId = new Map<string, { rationale: string }>();
  for (const row of rows) {
    if (typeof row.skill_id !== 'string' || row.skill_id.length === 0) continue;
    // Pre-toggle gate: keep only rows the scan is confident about.
    const conf = row.confidence;
    const above = conf === undefined || conf === null || conf >= SCAN_PRETOGGLE_CONFIDENCE;
    if (!above) continue;
    // First write wins (server post-validator already de-dupes by id).
    if (!byId.has(row.skill_id)) {
      byId.set(row.skill_id, {
        rationale: typeof row.rationale === 'string' ? row.rationale : '',
      });
    }
  }
  if (byId.size === 0) return baseline;

  const merged: SkillRecommendation[] = [];
  const seen = new Set<string>();
  for (const row of baseline) {
    const scanRow = byId.get(row.skill_id);
    if (scanRow !== undefined) {
      merged.push({
        skill_id: row.skill_id,
        rationale: scanRow.rationale.length > 0 ? scanRow.rationale : row.rationale,
        source: 'llm',
      });
    } else {
      merged.push(row);
    }
    seen.add(row.skill_id);
  }
  for (const [skill_id, scanRow] of byId) {
    if (seen.has(skill_id)) continue;
    merged.push({
      skill_id: sid(skill_id),
      rationale: scanRow.rationale,
      source: 'llm',
    });
    seen.add(skill_id);
  }
  return merged;
}

/* ================================================================== */
/* ADR-0226 — business-skill suggestions for non-store sites          */
/* ================================================================== */

/**
 * One suggested business skill for the onboarding Skills step: the
 * scan found a page of `kind` on the homepage at `url`. A SUGGESTION
 * only — nothing is saved or enabled until the merchant clicks
 * (ADR-0040 no-auto-apply). The URL is rendered as escaped React text /
 * an input value and re-validated server-side on save.
 */
export interface BusinessSkillSuggestion {
  readonly skill_id: BusinessSkillId;
  readonly kind: BusinessPageKind;
  readonly url: string;
}

/** Same cap the origin save route enforces (ADR-0222 §6). */
const BUSINESS_URL_MAX = 2048;

function isSuggestableUrl(url: unknown): url is string {
  if (typeof url !== 'string' || url.length === 0 || url.length > BUSINESS_URL_MAX) {
    return false;
  }
  try {
    return new URL(url).protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Map a scan's `key_pages` to business-skill suggestions. Same mapping
 * as the origin's `suggested_url` projection and the readiness
 * `detected_business_links` (first page per kind wins), in
 * `BUSINESS_SKILL_IDS` order. Only `https:` URLs under the save-route
 * length cap survive — anything else could never be saved anyway, and
 * an `https:`-only filter keeps a `javascript:` value out of any href.
 * Pure; never throws. A missing / running scan yields `[]`; a completed
 * one is read, and a still-running row's partial pages are ignored.
 */
export function suggestBusinessSkillsFromScan(
  scan: SiteScanRow | null,
): BusinessSkillSuggestion[] {
  if (scan === null || scan.status !== 'completed') return [];
  const pages = Array.isArray(scan.key_pages) ? scan.key_pages : [];
  const first_url = new Map<BusinessPageKind, string>();
  for (const page of pages) {
    const kind: unknown = (page as { kind?: unknown }).kind;
    if (!isBusinessPageKind(kind) || first_url.has(kind)) continue;
    const url: unknown = (page as { url?: unknown }).url;
    if (!isSuggestableUrl(url)) continue;
    first_url.set(kind, url);
  }
  return BUSINESS_PAGE_KINDS.flatMap((kind) => {
    const url = first_url.get(kind);
    return url === undefined
      ? []
      : [{ skill_id: BUSINESS_PAGE_KIND_TO_SKILL_ID[kind], kind, url }];
  });
}

/**
 * How the onboarding Skills step lays out for this site:
 *   - `'store'`   — the existing layout (store skills grid). Returned
 *     ONLY when something positively says store (connector connected,
 *     search-eligible catalog, or a cart-platform fingerprint from
 *     detection: Shopify, WooCommerce, BigCommerce, Magento).
 *   - `'website'` — business-skill suggestions first, the store skills
 *     tucked under a collapsed "More skills" expander. This is the
 *     DEFAULT: a non-store detection, a null detection, AND a pending
 *     detection all land here, so the merchant never sees the store
 *     grid flashed as the pre-detection fallback (founder 2026-10-21).
 */
export type OnboardingSkillsLayout = 'store' | 'website';

/** `other_known` vendors that are cart platforms (platform-detect.ts). */
const CART_VENDORS: ReadonlySet<string> = new Set(['bigcommerce', 'magento']);

export interface OnboardingSkillsLayoutInput {
  /** `undefined` while detection is in flight; `null` once it failed. */
  readonly detected: DetectPlatformResult | null | undefined;
  /** `derivePlatformKind(...)` — the same strict helper Settings → Skills
   *  uses to hide inert store rows. `null` while still resolving. */
  readonly platform_kind: ReadinessPlatformKind | null;
  /** Business pages found by the scan (see `suggestBusinessSkillsFromScan`). */
  readonly business_suggestion_count: number;
  /**
   * Optional: `detected_platform` from the stored site-scan row. Used as a
   * tiebreaker when `detectPlatform` could not read the page (WAF-blocked /
   * robots.txt-gated) but the scan already fingerprinted the CMS from a
   * prior fetch. A WordPress site behind a WAF or robots.txt gate would
   * otherwise fall through to 'store' (the safe default for unknown).
   */
  readonly scan_platform?: string | null;
}

/**
 * ADR-0226 — classify the site for the onboarding Skills step. A site
 * is laid out as `'store'` ONLY when something affirmatively says so:
 * `derivePlatformKind` reports `store` (a connected store connector or
 * a search-eligible catalog), OR platform detection fingerprinted a
 * cart platform (Shopify, WooCommerce, BigCommerce, Magento). Fresh
 * Shopify stores have no connector yet at this step, so detection's
 * cart fingerprint carries weight on its own.
 *
 * Everything else lays out as `'website'` (business-first) — including
 * every pre-detection state:
 *   - detection in flight (`detected === undefined`),
 *   - the platform read in flight (`platform_kind === null`),
 *   - detection returned `unknown` / `cloudflare` / WordPress-without-
 *     cart / failed,
 *   - the scan has not yet landed (the typical in-dashboard add-site
 *     path, where there is no pre-captured public_domain_scan).
 *
 * Previously `undefined`/`null` resolved to a `'pending'` state whose
 * UI then auto-opened the "More skills" expander as its fallback — so a
 * non-store site added from the dashboard (no scan yet) flashed the
 * seven ecommerce skills exactly where business skills should have
 * been. The two paths into Skills (sill.so hero → signup, and in-
 * dashboard Add site) now agree: nothing says store → business-first,
 * every time (founder 2026-10-21 on siteforbiz.com).
 *
 * Pure.
 */
export function classifyOnboardingSkillsLayout(
  input: OnboardingSkillsLayoutInput,
): OnboardingSkillsLayout {
  const { detected, platform_kind } = input;
  if (platform_kind === 'store') return 'store';
  if (detected !== null && detected !== undefined) {
    if (detected.platform === 'shopify' || detected.platform === 'woocommerce') {
      return 'store';
    }
    if (detected.platform === 'other_known') {
      const vendor = (detected.vendor_hint ?? '').toLowerCase();
      if (CART_VENDORS.has(vendor)) return 'store';
    }
  }
  // Nothing in the signal says store → business-first. The merchant can
  // still open "More skills" to reach the store grid.
  return 'website';
}
